import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configExcludedI18n } from './i18n';

const STATUS_VALUES = {
  active: 'active',
  inactive: 'inactive',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['inactive', 'inactivo', 'inactivos'].includes(normalized)) {
    return STATUS_VALUES.inactive;
  }

  return STATUS_VALUES.active;
};

export default function ExcludedCreateEditModal({
  Modal,
  Input,
  Dropdown,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const excludedLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configExcludedI18n });

  const isEdit = mode === 'edit';
  const [local, setLocal] = useState({
    id: null,
    name: '',
    status: STATUS_VALUES.active,
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    configExcludedI18n.changeLanguage(excludedLanguage);
  }, [excludedLanguage]);

  useEffect(() => {
    if (show) {
      setLocal({
        id: value?.id ?? null,
        name: value?.name ?? '',
        status: normalizeStatus(value?.status),
      });
      setErrors({});
    }
  }, [show, value]);

  const nameValid = useMemo(() => {
    const trimmed = local.name.trim();
    return trimmed.length > 0 && trimmed.length <= 200;
  }, [local.name]);

  const statusValid = useMemo(() => {
    return [STATUS_VALUES.active, STATUS_VALUES.inactive].includes(
      normalizeStatus(local.status)
    );
  }, [local.status]);

  const isValid = nameValid && statusValid;

  const setField = (key) => (eventOrValue) => {
    const nextValue = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: nextValue }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!local.name?.trim()) {
      newErrors.name = t('config_excluded_modal_error_name_required');
    } else if (local.name.trim().length > 200) {
      newErrors.name = t('config_excluded_modal_error_name_max');
    }

    if (!statusValid) {
      newErrors.status = t('config_excluded_modal_error_status_invalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        ...local,
        status: normalizeStatus(local.status),
      };

      onChange?.(payload);
      await Promise.resolve(onSave?.(payload));
    } finally {
      setIsSaving(false);
    }
  };

  if (!Modal || !Input) return null;

  const statusOptions = [
    { label: t('config_excluded_status_active'), value: STATUS_VALUES.active },
    {
      label: t('config_excluded_status_inactive'),
      value: STATUS_VALUES.inactive,
    },
  ];

  const statusOptionFrom = (val) =>
    statusOptions.find((option) => option.value === normalizeStatus(val)) ||
    statusOptions[0];

  return (
    <Modal
      title={
        isEdit
          ? t('config_excluded_modal_title_edit')
          : t('config_excluded_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isSaving
          ? t('config_excluded_modal_primary_saving')
          : isEdit
            ? t('config_excluded_modal_primary_edit')
            : t('config_excluded_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || isSaving}
      secondaryBtnTxt={t('config_excluded_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='640px'
    >
      <div className='row g-3'>
        <div className='col-12'>
          <Input
            label={t('config_excluded_modal_name_label')}
            required
            placeholder={t('config_excluded_modal_name_placeholder')}
            value={local.name}
            onChange={setField('name')}
            error={errors.name || ''}
            maxLength={200}
            helperText={t('config_excluded_modal_name_helper', {
              count: local.name.length,
            })}
          />
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>
            {t('config_excluded_modal_status_label')}{' '}
            <span className='text-danger'>*</span>
          </label>
          {Dropdown ? (
            <Dropdown
              options={statusOptions}
              value={statusOptionFrom(local.status)}
              onChange={(option) =>
                setLocal((state) => ({
                  ...state,
                  status: option?.value || STATUS_VALUES.active,
                }))
              }
              placeholder={t('config_excluded_modal_status_placeholder')}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.status}
              onChange={setField('status')}
            >
              <option value={STATUS_VALUES.active}>
                {t('config_excluded_status_active')}
              </option>
              <option value={STATUS_VALUES.inactive}>
                {t('config_excluded_status_inactive')}
              </option>
            </select>
          )}
          {errors.status && (
            <div className='text-danger small mt-1'>{errors.status}</div>
          )}
        </div>
      </div>
    </Modal>
  );
}
